import { getSupabaseClient } from '@/config/supabase';
import type {
  Organization,
  ManagerProfile,
  ManagerRole,
  OrganizationInvite,
  InvitePreview,
} from '@/types';

const ORG_SELECT = 'id, name, apple_org_id, enrollment_code';

export async function fetchOrganization(id: string): Promise<Organization> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from('organizations')
    .select(ORG_SELECT)
    .eq('id', id)
    .single();
  if (error) throw error;
  return data as Organization;
}

/** All organization ids — used to expand a super admin's scope to every org. */
export async function fetchAllOrganizationIds(): Promise<string[]> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.from('organizations').select('id');
  if (error) throw error;
  return (data ?? []).map((o) => (o as { id: string }).id);
}

/** Every organization — super-admin-only listing. */
export async function fetchAllOrganizations(): Promise<Organization[]> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from('organizations')
    .select(ORG_SELECT)
    .order('name');
  if (error) throw error;
  return (data ?? []) as Organization[];
}

/**
 * Create a new, empty organization. Super-admin-only — the RPC verifies the caller
 * is a super admin server-side. Returns the created org.
 */
export async function createOrganization(name: string): Promise<Organization> {
  const trimmed = name.trim();
  if (trimmed === '') throw new Error('Organization name is required');
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc('create_organization', { p_name: trimmed });
  if (error) throw error;
  return data as Organization;
}

/**
 * Signup path: atomically create a new organization and the owner manager profile
 * for the currently-authenticated Supabase user. The RPC reads the user id/email
 * from the auth context; returns the new owner profile.
 */
export async function createOrganizationAndOwner(params: {
  name: string;
  displayName: string;
}): Promise<ManagerProfile> {
  const trimmed = params.name.trim();
  if (trimmed === '') throw new Error('Organization name is required');
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc('create_organization_and_owner', {
    p_org_name: trimmed,
    p_display_name: params.displayName.trim(),
  });
  if (error) throw error;
  return data as ManagerProfile;
}

export interface OrganizationUpdates {
  name?: string;
  apple_org_id?: string | null;
  enrollment_code?: string | null;
}

function normalizeText(value: string | null | undefined, opts: { uppercase?: boolean } = {}): string | null | undefined {
  if (value === undefined) return undefined;
  if (value === null) return null;
  const trimmed = value.trim();
  if (trimmed === '') return null;
  return opts.uppercase ? trimmed.toUpperCase() : trimmed;
}

export async function updateOrganization(
  id: string,
  updates: OrganizationUpdates,
): Promise<Organization> {
  const name = updates.name?.trim();
  if (!name) throw new Error('Organization name cannot be empty');
  const supabase = getSupabaseClient();
  // Full update via SECURITY DEFINER RPC (gated to super admins + the org's own admins).
  // NULL clears apple_org_id / enrollment_code; the settings form always submits all three.
  const { data, error } = await supabase.rpc('update_organization', {
    p_org_id: id,
    p_name: name,
    p_apple_org_id: normalizeText(updates.apple_org_id) ?? null,
    p_enrollment_code: normalizeText(updates.enrollment_code, { uppercase: true }) ?? null,
  });
  if (error) throw error;
  return data as Organization;
}

/**
 * Permanently delete an organization and everything scoped to it — devices,
 * location history/visits, geofences, alerts, compliance data — and remove all of
 * its members. Members who belong ONLY to this organization have their account
 * deleted; members who also belong to other organizations are simply detached from
 * this one. Extremely destructive and irreversible.
 *
 * Gated server-side to super admins + the org's own admin managers via a
 * SECURITY DEFINER RPC — there is no client-side fallback (the cascade of deletes
 * across org-scoped tables and auth users cannot be done from the client under RLS).
 */
export async function deleteOrganization(id: string): Promise<void> {
  const supabase = getSupabaseClient();
  const { error } = await supabase.rpc('delete_organization', { p_org_id: id });
  if (error) throw error;
}

const MEMBER_SELECT =
  'id, azure_ad_id, email, display_name, role, is_super_admin, organization_ids';

export async function fetchOrganizationMembers(
  organizationId: string,
): Promise<ManagerProfile[]> {
  const supabase = getSupabaseClient();
  // `manager_profiles` is self-read-only under RLS, so reading other members requires
  // a SECURITY DEFINER RPC. Fall back to the direct (self-only) read if the RPC isn't
  // deployed yet (PGRST202) so the Org Settings page never hard-fails on the members card.
  const { data, error } = await supabase.rpc('get_organization_members', {
    p_org_id: organizationId,
  });
  if (error) {
    if (error.code === 'PGRST202') {
      const { data: fallback, error: fbError } = await supabase
        .from('manager_profiles')
        .select(MEMBER_SELECT)
        .contains('organization_ids', [organizationId])
        .order('display_name');
      if (fbError) throw fbError;
      return (fallback ?? []) as ManagerProfile[];
    }
    throw error;
  }
  return (data ?? []) as ManagerProfile[];
}

/**
 * Detach a member from a single organization (their account and any other org
 * memberships are untouched). Gated server-side to super admins + the org's own
 * admins; the RPC also refuses to remove the caller themselves or the org's last
 * admin. `manager_profiles` writes are RLS-restricted, so this must go through the
 * SECURITY DEFINER RPC — there is no client-side fallback.
 */
export async function removeOrganizationMember(
  organizationId: string,
  memberId: string,
): Promise<void> {
  const supabase = getSupabaseClient();
  const { error } = await supabase.rpc('remove_organization_member', {
    p_org_id: organizationId,
    p_member_id: memberId,
  });
  if (error) throw error;
}

/**
 * Mint a single-use, one-hour invite for someone to join this organization with
 * the given role. Super-admin / org-admin gated server-side. Returns the invite
 * row (the caller builds the shareable accept link from `token`).
 */
export async function createOrganizationInvite(params: {
  organizationId: string;
  email: string;
  role: ManagerRole;
}): Promise<OrganizationInvite> {
  const email = params.email.trim().toLowerCase();
  if (email === '') throw new Error('Email is required');
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc('create_organization_invite', {
    p_org_id: params.organizationId,
    p_email: email,
    p_role: params.role,
  });
  if (error) throw error;
  return data as OrganizationInvite;
}

/**
 * Pending (unaccepted, unexpired) invites for an organization. Returns [] if the
 * RPC isn't deployed yet (PGRST202) so the members card never hard-fails.
 */
export async function fetchOrganizationInvites(
  organizationId: string,
): Promise<OrganizationInvite[]> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc('list_organization_invites', {
    p_org_id: organizationId,
  });
  if (error) {
    if (error.code === 'PGRST202') return [];
    throw error;
  }
  return (data ?? []) as OrganizationInvite[];
}

/** Revoke a pending invite so its link can no longer be redeemed. Admin-gated. */
export async function revokeOrganizationInvite(inviteId: string): Promise<void> {
  const supabase = getSupabaseClient();
  const { error } = await supabase.rpc('revoke_organization_invite', {
    p_invite_id: inviteId,
  });
  if (error) throw error;
}

/**
 * Public, pre-signup lookup of an invite by its token — lets the accept page show
 * who/what the invite is for before the invitee creates their account. Callable by
 * the anon role (the token is the secret). Returns null when the token is unknown.
 */
export async function fetchInviteByToken(token: string): Promise<InvitePreview | null> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc('get_organization_invite', { p_token: token });
  if (error) throw error;
  if (!data) return null;
  // The RPC returns a single row (or a one-element set, depending on definition).
  const row = Array.isArray(data) ? data[0] : data;
  return (row as InvitePreview | undefined) ?? null;
}

/**
 * Redeem an invite for the currently-authenticated user: provisions / attaches
 * their manager profile to the invited org with the invited role, and marks the
 * invite used. Must run with a live Supabase session (auth.uid()/auth.email() are
 * read server-side and matched against the invite). Returns the resulting profile.
 */
export async function acceptOrganizationInvite(
  token: string,
  displayName: string,
): Promise<ManagerProfile> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc('accept_organization_invite', {
    p_token: token,
    p_display_name: displayName.trim(),
  });
  if (error) throw error;
  return data as ManagerProfile;
}
